import {
  ApplicationConfig,
  importProvidersFrom,
  Injectable,
  isDevMode,
  provideZoneChangeDetection,
  provideAppInitializer,
  inject,
  LOCALE_ID,
} from '@angular/core';
import { provideHttpClient, HttpClient, withInterceptors, withInterceptorsFromDi } from '@angular/common/http';
import { registerLocaleData } from '@angular/common';
import localePt from '@angular/common/locales/pt';
import localeEn from '@angular/common/locales/en';
import { Routes } from '@angular/router';
import {
  provideRouter,
  withComponentInputBinding,
  withInMemoryScrolling,
} from '@angular/router';
import { Translation, TranslocoLoader, provideTransloco } from '@jsverse/transloco';
import { ToastNoAnimation, provideToastr } from 'ngx-toastr';
import { NgScrollbarModule } from 'ngx-scrollbar';
import { NgxPermissionsModule } from 'ngx-permissions';
import { FormsModule } from '@angular/forms';
import { CalendarModule, DateAdapter } from 'angular-calendar';
import { adapterFactory } from 'angular-calendar/date-adapters/date-fns';
import { provideHighlightOptions } from 'ngx-highlightjs';
import { provideServiceWorker } from '@angular/service-worker';

import { MaterialModule } from '@ukam/ui-kit/material.module';
import { API_BASE_URL } from '@ukam/core/tokens/api-base-url.token';
import { APP_KEY } from '@ukam/core/tokens/app-key.token';
import { NavItem } from '@ukam/shell/layouts/full/vertical/sidebar/nav-item/nav-item';
import { NAV_ITEMS } from '@ukam/shell/nav/nav-items.token';
import { AppSettings } from '@ukam/shell/config';
import { APP_SETTINGS_DEFAULTS } from '@ukam/shell/app-settings.token';
import { authInterceptor } from '@ukam/shell/interceptors/auth.interceptor';
import { AuthService } from '@ukam/shell/services/auth.service';
import { CredentialService } from '@ukam/core/services/credential.service';
import { PwaUpdateService } from '@ukam/shell/services/pwa-update.service';

@Injectable({ providedIn: 'root' })
export class CustomLoader implements TranslocoLoader {
  private http = inject(HttpClient);
  getTranslation(lang: string) {
    return this.http.get<Translation>(`./assets/i18n/${lang}.json`);
  }
}

registerLocaleData(localePt, 'pt');
registerLocaleData(localePt, 'pt-BR');
registerLocaleData(localeEn, 'en');
registerLocaleData(localeEn, 'en-US');

const SUPPORTED_LOCALES = ['pt-BR', 'pt', 'en-US', 'en'];

// Usa o idioma configurado no navegador do usuário para formatar número, moeda e data,
// em vez do 'en-US' fixo que o Angular assume quando nenhum LOCALE_ID é fornecido.
function resolveBrowserLocale(): string {
  const browserLocale = navigator.language;
  if (SUPPORTED_LOCALES.includes(browserLocale)) return browserLocale;
  const lang = browserLocale.split('-')[0];
  return SUPPORTED_LOCALES.find((locale) => locale.startsWith(lang)) ?? 'pt-BR';
}

export interface AppSetup {
  /** Base da API, vinda do environment da própria aplicação. */
  apiBaseUrl: string;
  appKey: string;
  /** Rotas da aplicação — cada app compõe as features que oferece. */
  routes: Routes;
  /** Itens do menu — compostos a partir dos fragmentos das libs de feature. */
  navItems: NavItem[];
  /** Configurações padrão da aplicação (tema, logo, ...). Sobrepõe `defaults` do shell. */
  appSettings?: Partial<AppSettings>;
}

// Bootstrap comum a todas as aplicações. Cada app só informa o que a distingue:
// sua API, suas rotas e seu menu.
export function createAppConfig(setup: AppSetup): ApplicationConfig {
  return {
    providers: [
      { provide: API_BASE_URL, useValue: setup.apiBaseUrl },
      { provide: APP_KEY, useValue: setup.appKey },
      { provide: NAV_ITEMS, useValue: setup.navItems },
      { provide: APP_SETTINGS_DEFAULTS, useValue: {...setup.appSettings } },
      { provide: LOCALE_ID, useFactory: resolveBrowserLocale },
      provideToastr({ toastComponent: ToastNoAnimation }), // Toastr providers (no Angular animations)
      provideZoneChangeDetection({ eventCoalescing: true }),
      provideHighlightOptions({
        coreLibraryLoader: () => import('highlight.js/lib/core'),
        lineNumbersLoader: () => import('ngx-highlightjs/line-numbers'), // Optional, add line numbers if needed
        languages: {
          typescript: () => import('highlight.js/lib/languages/typescript'),
          css: () => import('highlight.js/lib/languages/css'),
          xml: () => import('highlight.js/lib/languages/xml'),
        },
      }),
      provideRouter(
        setup.routes,
        withInMemoryScrolling({
          scrollPositionRestoration: 'enabled',
          anchorScrolling: 'enabled',
        }),
        withComponentInputBinding()
      ),
      provideHttpClient(withInterceptors([authInterceptor]), withInterceptorsFromDi()),
      provideAppInitializer(() => {
        const authService = inject(AuthService);
        const credentialService = inject(CredentialService);

        if (authService.isAuthenticated() && !authService.isPasswordChangeRequired()) {
          credentialService.load().subscribe();
        }

        inject(PwaUpdateService).init();
      }),
      // Service Worker: ativo apenas nos builds de produção; registrado depois
      // que a aplicação estabiliza para não atrasar o primeiro carregamento.
      provideServiceWorker('ngsw-worker.js', {
        enabled: !isDevMode(),
        registrationStrategy: 'registerWhenStable:30000',
      }),
      provideTransloco({
        config: {
          availableLangs: ['pt', 'en'],
          defaultLang: 'pt',
          fallbackLang: 'pt',
          reRenderOnLangChange: true,
          prodMode: !isDevMode(),
          missingHandler: { logMissingKey: false },
        },
        loader: CustomLoader,
      }),
      importProvidersFrom(
        FormsModule,
        MaterialModule,
        NgxPermissionsModule.forRoot(),
        NgScrollbarModule,
        CalendarModule.forRoot({
          provide: DateAdapter,
          useFactory: adapterFactory,
        })
      ),
    ],
  };
}
